/**
 * Importação do extrato de pedágio (Sem Parar).
 *
 * Fluxo em duas etapas de propósito: o gestor sobe o PDF, vê a PRÉVIA — período
 * real, placas, valores, quais placas não estão no cadastro — e só então
 * confirma. Import de custo é irreversível na percepção de quem usa; descobrir
 * o que entrou depois de gravar é tarde.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";

interface ItemPrevia {
  plate: string;
  tolls: number;
  tollCount: number;
  total: number;
  cadastrada: boolean;
}

interface Previa {
  ok: boolean;
  problemas: string[];
  invoiceNumber: string;
  substituiu: boolean;
  periodStart: string | null;
  periodEnd: string | null;
  subtotal: number | null;
  total: number | null;
  placas: number;
  passagens: number;
  placasSemCadastro: string[];
  itens: ItemPrevia[];
}

interface FaturaImportada {
  id: string;
  invoiceNumber: string;
  source: string;
  periodStart: string | null;
  periodEnd: string | null;
  dueDate: string | null;
  subtotal: number | null;
  credits: number | null;
  total: number | null;
  fileName: string | null;
  importedAt: string;
  plates: number;
}

const brl = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const dia = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";

export default function TollImportPage() {
  const qc = useQueryClient();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { data: faturas } = useQuery({
    queryKey: ["tolls", "invoices"],
    queryFn: () => api.get<{ invoices: FaturaImportada[] }>("/tolls/invoices"),
  });

  const analisar = useMutation({
    mutationFn: (f: File) => api.upload<Previa>("/tolls/analyze", f),
    onSuccess: (r) => { setPrevia(r); setErro(null); },
    onError: (e: ApiError) => { setPrevia(null); setErro(e.message); },
  });

  const importar = useMutation({
    mutationFn: (f: File) => api.upload<Previa>("/tolls/import", f),
    onSuccess: (r) => {
      setPrevia(r);
      setErro(null);
      void qc.invalidateQueries({ queryKey: ["tolls", "invoices"] });
      // O raio-X mostra o pedágio pago; sem invalidar, ele seguiria com o
      // número de antes do import.
      void qc.invalidateQueries({ queryKey: ["transport", "vehicle-drilldown"] });
    },
    onError: (e: ApiError) => {
      const d = e.details as Previa | undefined;
      if (d?.problemas?.length) { setPrevia(d); setErro(null); }
      else setErro(e.message);
    },
  });

  function escolher(f: File | null) {
    setArquivo(f);
    setPrevia(null);
    setErro(null);
    if (f) analisar.mutate(f);
  }

  const ocupado = analisar.isPending || importar.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Importar extrato de pedágio"
        subtitle="Sem Parar — o valor pago na praça, para auditar contra o pedágio cobrado no frete"
      />

      <div className="card p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Arquivo do extrato (PDF)
          </label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            disabled={ocupado}
            onChange={(e) => escolher(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg
                       file:border-0 file:text-sm file:font-medium file:bg-tm-50 file:text-tm-700
                       hover:file:bg-tm-100 disabled:opacity-50"
          />
          <p className="text-xs text-gray-500 mt-2">
            Baixe o extrato no portal Sem Parar Empresas e suba aqui. Reimportar a mesma fatura
            substitui os valores — não duplica.
          </p>
        </div>

        {analisar.isPending && <p className="text-sm text-gray-600">Lendo o arquivo…</p>}
        {erro && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{erro}</div>
        )}
      </div>

      {previa && (
        <div className="card p-5 space-y-4">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-gray-900">
              Fatura {previa.invoiceNumber}
            </p>
            <p className="text-xs text-gray-500">
              Período coberto: {dia(previa.periodStart)} a {dia(previa.periodEnd)}
            </p>
          </div>

          {/* Problemas bloqueiam a gravação: um extrato que não fecha consigo
              mesmo viraria custo errado com cara de certo. */}
          {previa.problemas.length > 0 ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="font-medium mb-1">Este arquivo não foi aceito:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {previa.problemas.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500">Placas</p>
                <p className="text-lg font-semibold text-gray-900">{previa.placas}</p>
                <p className="text-xs text-gray-500" title="Passagens com data lidas do detalhamento — é o que entra na margem, mês a mês">
                  {previa.passagens} passagem(ns) datada(s)
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Subtotal</p>
                <p className="text-lg font-semibold text-gray-900">{brl(previa.subtotal)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total da nota</p>
                <p className="text-lg font-semibold text-gray-900">{brl(previa.total)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Sem cadastro</p>
                <p className={`text-lg font-semibold ${previa.placasSemCadastro.length ? "text-amber-600" : "text-gray-900"}`}>
                  {previa.placasSemCadastro.length}
                </p>
              </div>
            </div>
          )}

          {previa.placasSemCadastro.length > 0 && (
            <p className="text-xs text-amber-700">
              ⚠ Estas placas estão no extrato mas não no cadastro do SinerSoft, então o valor delas
              não vai aparecer em nenhum raio-X: {previa.placasSemCadastro.join(", ")}. O import
              continua válido — o dado fica guardado e passa a aparecer quando a placa entrar no
              cadastro.
            </p>
          )}

          {previa.problemas.length === 0 && (
            <>
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-xs text-gray-600">
                      <th className="px-3 py-2">Placa</th>
                      <th className="px-3 py-2 text-right">Pedágio</th>
                      <th className="px-3 py-2 text-right">Passagens</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previa.itens.map((i) => (
                      <tr key={i.plate} className="border-t border-gray-100">
                        <td className="px-3 py-1.5">
                          {i.cadastrada ? (
                            <Link to={`/frota/placa/${encodeURIComponent(i.plate)}`} className="font-medium text-tm-700 hover:underline">{i.plate}</Link>
                          ) : (
                            i.plate
                          )}
                          {!i.cadastrada && (
                            <span className="ml-2 text-xs text-amber-600">fora do cadastro</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right">{brl(i.tolls)}</td>
                        <td className="px-3 py-1.5 text-right text-gray-500">{i.tollCount}</td>
                        <td className="px-3 py-1.5 text-right font-medium">{brl(i.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={ocupado || !arquivo}
                  onClick={() => arquivo && importar.mutate(arquivo)}
                  className="px-4 py-2 rounded-lg bg-tm-600 text-white text-sm font-medium
                             hover:bg-tm-700 disabled:opacity-50"
                >
                  {importar.isPending ? "Importando…" : "Confirmar importação"}
                </button>
                {importar.isSuccess && (
                  <span className="text-sm text-emerald-700">
                    ✓ {previa.substituiu ? "Fatura atualizada" : "Fatura importada"} — {previa.placas} placa(s).
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="card p-5">
        <p className="text-sm font-semibold text-gray-900 mb-3">Faturas já importadas</p>
        {faturas?.invoices.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-600 border-b border-gray-200">
                <th className="py-2">Fatura</th>
                <th className="py-2">Período coberto</th>
                <th className="py-2">Vencimento</th>
                <th className="py-2 text-right">Placas</th>
                <th className="py-2 text-right">Subtotal</th>
                <th className="py-2 text-right">Total</th>
                <th className="py-2">Importada em</th>
              </tr>
            </thead>
            <tbody>
              {faturas.invoices.map((f) => (
                <tr key={f.id} className="border-b border-gray-100">
                  <td className="py-2">{f.invoiceNumber}</td>
                  <td className="py-2">{dia(f.periodStart)} a {dia(f.periodEnd)}</td>
                  <td className="py-2">{dia(f.dueDate)}</td>
                  <td className="py-2 text-right">{f.plates}</td>
                  <td className="py-2 text-right">{brl(f.subtotal)}</td>
                  <td className="py-2 text-right font-medium">{brl(f.total)}</td>
                  <td className="py-2 text-gray-500">
                    {new Date(f.importedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-gray-500">Nenhum extrato importado ainda.</p>
        )}
      </div>
    </div>
  );
}
